/**
 * PlaceholderExtension - Custom TipTap extension for workflow placeholders
 * 
 * Supports multiple placeholder types:
 * - ${} - Workflow placeholders
 * - !{} - Form placeholders  
 * - ={} - Function placeholders
 * 
 * Features:
 * - Autocomplete suggestions triggered by $, !, or =
 * - Placeholder chip rendering with color-coding
 * - Error state for invalid/unavailable placeholders
 */

import { Node, mergeAttributes, Extension } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { NodeViewWrapper } from '@tiptap/react';
import getPlaceholderColors, { AGENT_MENTION_COLORS } from '../../utils/placeholderColors';
import { PLACEHOLDER_ERROR_COLOR } from '../../utils/PlaceholderUtil';
import useTracePlaceholder from '../Placeholders/useTracePlaceholder';

/**
 * React component for rendering placeholder chips in the editor.
 * Uses useTheme() for reliable dark mode detection since this component
 * is rendered inside the ThemeProvider via ReactNodeViewRenderer.
 */
export const PlaceholderChipComponent = ({ node, updateAttributes, selected }) => {
  const { prefix, key, displayKey, type, isValid } = node.attrs;
  const chipLabel = displayKey || key;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { onChipClick } = useTracePlaceholder();
  const handleChipMouseDown = (e) => {
    if (!key || prefix === '@') return;
    e.preventDefault();
    e.stopPropagation();
    onChipClick(key, type);
  };

  if (prefix === '@') {
    return (
      <NodeViewWrapper
        as="span"
        className={`placeholder-chip agent-mention-chip ${selected ? 'selected' : ''}`}
        style={{
          backgroundColor: isDark ? AGENT_MENTION_COLORS.bgDark : AGENT_MENTION_COLORS.bg,
          borderColor: isDark ? 'rgba(139, 92, 246, 0.4)' : AGENT_MENTION_COLORS.border,
          color: isDark ? AGENT_MENTION_COLORS.textDark : AGENT_MENTION_COLORS.text,
          paddingLeft: '8px',
        }}
        data-placeholder-key={key}
        data-placeholder-prefix="@"
        data-placeholder-type="agent"
      >
        @{key}
      </NodeViewWrapper>
    );
  }

  const colors = getPlaceholderColors(type, undefined, isDark);

  let chipBg, chipBorder, chipColor, iconUrl, iconColor;
  if (!isValid) {
    chipBg = isDark ? 'rgba(239, 68, 68, 0.15)' : PLACEHOLDER_ERROR_COLOR.BACKGROUND;
    chipBorder = isDark ? 'rgba(239, 68, 68, 0.5)' : PLACEHOLDER_ERROR_COLOR.BORDER;
    chipColor = isDark ? '#f87171' : PLACEHOLDER_ERROR_COLOR.TEXT;
    iconUrl = PLACEHOLDER_ERROR_COLOR.ICON;
    iconColor = chipColor;
  } else {
    chipBg = colors.hover;
    chipBorder = isDark ? `${colors.default}66` : colors.default;
    chipColor = colors.text;
    iconUrl = colors.icon;
    iconColor = colors.default;
  }

  return (
    <NodeViewWrapper
      as="span"
      className={`placeholder-chip ${selected ? 'selected' : ''} ${!isValid ? 'invalid' : ''}`}
      style={{
        backgroundColor: chipBg,
        borderColor: chipBorder,
        color: chipColor,
      }}
      data-placeholder-key={key}
      data-placeholder-prefix={prefix}
      data-placeholder-type={type}
      data-placeholder-role="reference"
      onMouseDown={handleChipMouseDown}
    >
      <span
        className="placeholder-chip-icon"
        style={{
          position: 'absolute',
          left: '7px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '14px',
          height: '14px',
          backgroundColor: iconColor,
          maskImage: `url(${iconUrl})`,
          WebkitMaskImage: `url(${iconUrl})`,
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
          opacity: isDark ? 0.9 : 0.85,
        }}
      />
      {prefix}&#123;{chipLabel}&#125;
    </NodeViewWrapper>
  );
};

/**
 * Custom TipTap Node for Placeholder chips
 */
export const PlaceholderNode = Node.create({
  name: 'placeholderNode',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      prefix: {
        default: '$',
      },
      key: {
        default: '',
      },
      displayKey: {
        default: null,
      },
      type: {
        default: 'text',
      },
      isValid: {
        default: true,
      },
      id: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-placeholder-key]',
        getAttrs: (element) => {
          return {
            key: element.getAttribute('data-placeholder-key'),
            displayKey: element.getAttribute('data-placeholder-display-key') || null,
            prefix: element.getAttribute('data-placeholder-prefix') || '$',
            type: element.getAttribute('data-placeholder-type') || 'text',
            isValid: element.getAttribute('data-placeholder-valid') !== 'false',
            id: element.getAttribute('data-placeholder-id') || null,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { prefix, key, displayKey } = node.attrs;
    const attrs = {
      'data-placeholder-key': node.attrs.key,
      'data-placeholder-prefix': node.attrs.prefix,
      'data-placeholder-type': node.attrs.type,
      'data-placeholder-valid': node.attrs.isValid ? 'true' : 'false',
      class: 'placeholder-chip',
    };
    if (displayKey) {
      attrs['data-placeholder-display-key'] = displayKey;
    }
    if (node.attrs.id) {
      attrs['data-placeholder-id'] = node.attrs.id;
    }
    const visibleKey = displayKey || key;
    const label = prefix === '@' ? `@${visibleKey}` : `${prefix}{${visibleKey}}`;
    return ['span', mergeAttributes(HTMLAttributes, attrs), label];
  },

  renderText({ node }) {
    const { prefix, key, id } = node.attrs;
    if (prefix === '@' && id) {
      return `@agent(${id})`;
    }
    return `${prefix}{${key}}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderChipComponent);
  },

  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          const { prefix, key, id } = node.attrs;
          if (prefix === '@' && id) {
            state.write(`@agent(${id})`);
          } else {
            state.write(`${prefix}{${key}}`);
          }
        },
        parse: {},
      },
    };
  },
});

/**
 * Creates a suggestion configuration for a specific trigger character
 * @param {string} triggerChar - The character that triggers the autocomplete ($, !, or =)
 * @param {Function} getItems - Function to get available placeholder items
 * @param {Object} component - The PlaceholderList component reference
 */
export const createPlaceholderSuggestion = (triggerChar, getItems, component) => {
  return {
    char: triggerChar,
    allowSpaces: false,
    startOfLine: false,
    
    items: ({ query }) => {
      const items = getItems(triggerChar);
      if (!items || !Array.isArray(items)) return [];
      
      return items
        .filter(item => {
          const searchText = `${triggerChar}{${item.key}}`;
          return searchText.toLowerCase().includes(query.toLowerCase()) ||
                 item.key.toLowerCase().includes(query.toLowerCase());
        })
        .slice(0, 10); // Limit to 10 suggestions
    },

    render: () => {
      let reactRenderer;
      let popup;

      return {
        onStart: (props) => {
          if (!component) return;
          
          reactRenderer = component.onStart(props);
          popup = component.popup;
        },

        onUpdate: (props) => {
          if (!reactRenderer) return;
          component.onUpdate(props);
        },

        onKeyDown: (props) => {
          if (!reactRenderer) return false;
          
          if (props.event.key === 'Escape') {
            component.onExit();
            return true;
          }

          return component.onKeyDown(props);
        },

        onExit: () => {
          if (component) {
            component.onExit();
          }
        },
      };
    },

    command: ({ editor, range, props }) => {
      const { key, type, label } = props;
      
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({
          type: 'placeholderNode',
          attrs: {
            prefix: triggerChar,
            key: key,
            displayKey: label && label !== key ? label : null,
            type: type || 'text',
            isValid: true,
          },
        })
        .run();
    },
  };
};

// Create unique PluginKeys for each trigger character
const suggestionPluginKeys = {
  '$': new PluginKey('suggestion_dollar'),
  '!': new PluginKey('suggestion_bang'),
  '=': new PluginKey('suggestion_equals'),
  '@': new PluginKey('suggestion_at'),
};

/**
 * Creates a unique PlaceholderSuggestion extension for a specific trigger character
 * Each trigger char needs its own extension with a unique name to avoid conflicts
 */
export const createPlaceholderSuggestionExtension = (triggerChar) => {
  // Map trigger chars to safe names for the extension
  const charNames = { '$': 'dollar', '!': 'bang', '=': 'equals', '@': 'at' };
  const safeName = charNames[triggerChar] || triggerChar;
  
  // Get or create a plugin key for this trigger
  const pluginKey = suggestionPluginKeys[triggerChar] || new PluginKey(`suggestion_${safeName}`);
  
  return Extension.create({
    name: `placeholderSuggestion_${safeName}`,

    addOptions() {
      return {
        suggestion: {
          char: triggerChar,
          items: () => [],
          render: () => ({}),
          command: () => {},
        },
      };
    },

    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          pluginKey: pluginKey,
          ...this.options.suggestion,
        }),
      ];
    },
  });
};

// Keep backward compatibility - default $ suggestion
export const PlaceholderSuggestionExtension = createPlaceholderSuggestionExtension('$');

export default PlaceholderNode;
